/**
 * Handoff of the conversation from the main chat (`/chatbot`) to the questions
 * panel.
 *
 * `/chatbot` fills the whole shell — every guided navigation leaves it
 * (`PORTAL_GUIDE.md` §6.7). Until now the user then landed on the target page
 * **without** a chat. This handoff closes the gap: when jumping away from the
 * chat, the navigator registers which conversation should come along;
 * `AppShell` then opens the right-hand questions panel, and `OverlayBotChat`
 * takes over the conversation including its history.
 *
 * Deliberately **not a jotai atom**, even though `State.ts` is otherwise the
 * house style: `index.tsx` mounts `<Provider>` without its own store, so the
 * store only lives in the React tree. But `useGuideNavigate` is also called from
 * inside the message renderer, and the pure helpers here should be testable
 * without a DOM. The shape is therefore the same as the self-navigation marker
 * in `useGuideNavigate.ts`: module state with observers, plus
 * `useSyncExternalStore` for the components.
 *
 * **Expiry.** A handoff nobody picks up (the jump was aborted, the target route
 * renders no panel, the user reloads) must not pop the panel open out of
 * nowhere minutes later. It is therefore only valid for `CHAT_HANDOFF_TTL_MS`;
 * after that it is moot without anyone having to clean up — the reading side
 * checks the age, not a timer.
 *
 * **Once-only.** Consumption goes by the handoff's `id`, not a boolean one-shot
 * flag: under `<React.StrictMode>` effects run twice in development mode, and a
 * flag would already be spent on the second run. Same solution as for
 * `isSelfNavigation`.
 */

import { useSyncExternalStore } from 'react';

import { MOBILE_BREAKPOINT } from '../hooks/use-mobile';
import { foldAddress } from './address';

/** Deadline within which a registered handoff must be picked up. */
export const CHAT_HANDOFF_TTL_MS = 10_000;

export interface ChatHandoff {
    /** Unique per registration — the basis for idempotent takeover. */
    id: string;
    /** The conversation that comes along; `null` if the address didn't carry one yet. */
    conversationId: string | null;
    /** Today there is only one origin; the field keeps the intent readable. */
    from: 'main';
    requestedAt: number;
    /**
     * Jump target as `pathname + search` — **binding**, not mere traceability:
     * a handoff may only be redeemed where it was registered for
     * (`isChatHandoffFor`). Without this binding, an unclaimed handoff (the
     * target lay outside the shell: `/markup`, `/logout`) would, within its
     * deadline, pop the panel open on the next page the user navigated to *on
     * their own* and adopt the main-chat thread there. The same mix-up that
     * `markSelfNavigation` guards against by carrying its target.
     */
    target: string;
}

let pending: ChatHandoff | null = null;
let sequence = 0;
const listeners = new Set<() => void>();

function publish(next: ChatHandoff | null): void {
    if (pending === next) return;
    pending = next;
    listeners.forEach((listener) => listener());
}

/**
 * Registers a handoff — **before** `markSelfNavigation`/`navigate`, so the panel
 * already sees it on the first render of the target page.
 */
export function requestChatHandoff(
    request: { conversationId?: string | null; target: string },
    now: number = Date.now(),
): ChatHandoff {
    const handoff: ChatHandoff = {
        id: `handoff-${now.toString(36)}-${(sequence += 1)}`,
        conversationId: request.conversationId || null,
        from: 'main',
        requestedAt: now,
        target: request.target,
    };
    publish(handoff);
    return handoff;
}

/** True as long as the handoff is within its deadline. */
export function isChatHandoffFresh(
    handoff: ChatHandoff | null,
    now: number = Date.now(),
): boolean {
    if (!handoff) return false;
    const age = now - handoff.requestedAt;
    return age >= 0 && age < CHAT_HANDOFF_TTL_MS;
}

/** The pending handoff, if still valid — without consuming it. */
export function peekChatHandoff(now: number = Date.now()): ChatHandoff | null {
    return isChatHandoffFresh(pending, now) ? pending : null;
}

/**
 * Picks up the handoff and clears it. An expired handoff is removed as well,
 * but not returned.
 */
export function consumeChatHandoff(now: number = Date.now()): ChatHandoff | null {
    const fresh = peekChatHandoff(now);
    publish(null);
    return fresh;
}

/**
 * Tests only: hard-reset the handoff. The logout path does **not** call this
 * today — the target binding (`isChatHandoffFor`) and the 10 s deadline limit
 * the damage of a leftover handoff to a user who logs in immediately afterwards
 * opening exactly the same address.
 */
export function resetChatHandoff(): void {
    publish(null);
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/**
 * Deliberately the **raw** module state, not the freshness check: a
 * `getSnapshot` that reads the clock would return different values within the
 * same render pass and send React into an infinite loop. The callers
 * (`shouldOpenAssistantForHandoff`, `isChatHandoffFresh`) decide about expiry
 * in the effect.
 */
function snapshot(): ChatHandoff | null {
    return pending;
}

export function useChatHandoff(): ChatHandoff | null {
    return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/**
 * Does this handoff apply *here* — fresh **and** at exactly the address it was
 * registered for? The comparison is folded (`foldAddress`): letter case and
 * parameter order are the same view for react-router.
 *
 * The target binding is the difference between "the conversation comes along"
 * and "a panel pops open somewhere": without it, a handoff whose target renders
 * no panel at all (`/markup`, `/map-and-image`, `/logout`) would open, within
 * its deadline, on the next shell page the user navigated to on their own.
 */
export function isChatHandoffFor(
    handoff: ChatHandoff | null,
    current: string,
    now: number = Date.now(),
): boolean {
    if (!isChatHandoffFresh(handoff, now)) return false;
    return foldAddress(handoff!.target) === foldAddress(current);
}

/**
 * May the questions panel open because of this handoff? Not on `/chatbot` —
 * `AppShell` renders no panel there; the chat itself is the assistant.
 * `current` is the address the caller is currently on (`pathname + search`, as
 * `useLocation` returns it).
 */
export function shouldOpenAssistantForHandoff(
    handoff: ChatHandoff | null,
    onChat: boolean,
    current: string,
    now: number = Date.now(),
): boolean {
    if (onChat) return false;
    return isChatHandoffFor(handoff, current, now);
}

/**
 * Should the transition run as a morph? No with `prefers-reduced-motion` (then
 * it just switches) and no on narrow windows: there the panel is a Radix sheet
 * attached as a portal to `body` — between two portal worlds there is no shared
 * rectangle to morph from.
 */
export function shouldMorph(reducedMotion: boolean, isMobile: boolean): boolean {
    return !reducedMotion && !isMobile;
}

/** What the navigator reads about the environment on click. */
export interface MorphEnvironment {
    reducedMotion: boolean;
    viewportWidth: number;
}

/**
 * Read the environment **once**, in the click handler — deliberately without
 * hooks. `useGuideNavigate` hangs off every single Markdown link of every
 * message; `useIsMobile` would create a `matchMedia` observer per link, and
 * `useReducedMotion` a second one.
 */
export function readMorphEnvironment(): MorphEnvironment {
    let reducedMotion = false;
    try {
        reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
        reducedMotion = false;
    }
    return { reducedMotion, viewportWidth: window.innerWidth };
}

/**
 * The same decision as `shouldMorph`, just against the *measured* environment.
 * The threshold comes from `use-mobile.tsx` and is not written twice:
 * `useIsMobile()` measures reactively, this measures on click — if the two
 * numbers drift apart, a flight starts without a landing area.
 */
export function shouldMorphNow(
    environment: MorphEnvironment = readMorphEnvironment(),
): boolean {
    return shouldMorph(environment.reducedMotion, environment.viewportWidth < MOBILE_BREAKPOINT);
}
